import React, { Suspense } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { Await, useLoaderData, useLocation, useRouteLoaderData } from 'react-router-dom'

import ActivityMenuItem from '@components/myScores/MyScoresMenu/ActivityMenuItem'
import CollectionsMenu from '@components/myScores/MyScoresMenu/CollectionsMenu'
import PremiumAd from '@components/myScores/MyScoresMenu/PremiumAd'
import RedeemedContentMenu from '@components/myScores/MyScoresMenu/RedeemedContentMenu'
import ContentItem from '@components/shared/ContentItem'
import NFIcon from '@components/shared/NFIcon'
import Divider from '@layout/Divider'
import MenuItem from '@layout/MenuItem'
import { useSession } from '@stores/globalData/globalData.store'
import { formatCommas } from '@utils/formatters/number.formatter'
import { isPresent } from '@utils/helpers'

import type { HomeLoaderResponse, PrivateStatisticsResponse } from '@pages/home/loaders/home.loader'
import type {
  TemplateCopiesResponse,
  TemplateLoaderResponse,
} from '@pages/home/loaders/template.loader'

interface Props {
  showMobile: boolean
  onMobileHide: () => void
}
const MyScoresMenu: React.FC<Props> = ({ showMobile, onMobileHide }) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const session = useSession()

  const data = useLoaderData() as HomeLoaderResponse
  const templateData = useRouteLoaderData('templateCopies') as TemplateLoaderResponse
  return (
    <>
      {/* Mobile backdrop */}
      {showMobile && (
        <button
          type="button"
          onClick={onMobileHide}
          className="fixed inset-0 block lg:hidden bg-black bg-opacity-20 z-20 backdrop-blur-[1px]"
        />
      )}

      <div
        className={classNames(
          'max-w-[300px] fixed flex flex-col w-full h-full overflow-y-auto overflow-x-visible z-50 lg:z-auto transition-margin duration-300 ease-in-out bg-white dark:bg-black-bg2 lg:bg-transparent lg:dark:bg-transparent top-0 bottom-0 shadow-black',
          'lg:relative lg:w-full lg:max-w-[320px] lg:transition-none lg:!ml-0 lg:bg-transparent lg:shadow-none',
          showMobile ? 'ml-0' : 'ml-[-300px]'
        )}>
        {/* This inner div is needed because of mobile Safari issues */}
        <div className="absolute inset-0 px-5 pb-5 pt-0 flex flex-col lg:pt-16 lg:pb-4 lg:pl-7 lg:pr-10 overflow-y-auto">
          <div className="w-full min-h-[4rem] flex flex-row justify-between lg:hidden">
            <p className="font-medium text-lg mt-5">{t('siteSidebar.home')}</p>
            <button type="button" onClick={onMobileHide}>
              <NFIcon icon="close" className="text-xl" />
            </button>
          </div>

          {/* My Scores */}
          <MenuItem
            label={t('titles.myScores')}
            to="/home/myScores"
            icon={<NFIcon icon="myscores-alt" className="text-xl" />}
            rightContent={<AsyncStat statistics={data.statistics} name="private_scores_count" />}
            selected={pathname === '/home/myScores' || pathname === '/home'}
            className="mb-1"
          />

          {/* My Books */}
          <Suspense fallback={null}>
            <Await resolve={data.statistics} errorElement={null}>
              {(stats: PrivateStatisticsResponse) => {
                if (!stats.my_books_count) return null
                return (
                  <MenuItem
                    label={t('titles.myBooks')}
                    to="/home/myBooks"
                    icon={<NFIcon icon="book" className="text-xl" />}
                    rightContent={stats.my_books_count}
                    selected={pathname === '/home/myBooks'}
                    className="mb-1"
                  />
                )
              }}
            </Await>
          </Suspense>

          {/* Purchased Scores */}
          <MenuItem
            label={t('titles.purchasedScores')}
            to="/home/purchasedScores"
            icon={<NFIcon icon="bag" className="text-xl" />}
            rightContent={<AsyncStat statistics={data.statistics} name="purchased_scores_count" />}
            selected={pathname === '/home/purchasedScores'}
            className="mb-1"
          />

          {/* Favorites */}
          <MenuItem
            label={t('titles.favoriteScores')}
            to="/home/favorites"
            icon={<NFIcon icon="favorite-outline" className="text-xl" />}
            rightContent={<AsyncStat statistics={data.statistics} name="favorites_count" />}
            selected={pathname === '/home/favorites'}
            className="mb-1"
          />

          {/* Scores Shared with Me */}
          <MenuItem
            label={t('titles.sharedScores')}
            to="/home/sharedWithMe"
            icon={<NFIcon icon="share-alt" className="text-xl" />}
            rightContent={<AsyncStat statistics={data.statistics} name="shared_scores_count" />}
            selected={pathname === '/home/sharedWithMe'}
            className="mb-1"
          />

          {/* Deleted */}
          <MenuItem
            label={t('titles.deletedScores')}
            to="/home/deletedScores"
            icon={<NFIcon icon="delete" className="text-xl" />}
            rightContent={<AsyncStat statistics={data.statistics} name="trash_count" />}
            selected={pathname === '/home/deletedScores'}
            className="mb-1"
          />
          <Divider dark narrow />

          {/* Templates */}
          {templateData && (
            <>
              <Suspense
                fallback={
                  <MenuItem
                    label={t('titles.scoresFromTemplate')}
                    to="#"
                    icon={<NFIcon icon="puzzle-alt" className="text-xl" />}
                    selected
                    className="mb-1"
                  />
                }>
                <Await resolve={templateData.templateCopies}>
                  {({ template }: TemplateCopiesResponse) => (
                    <MenuItem
                      label={t('titles.scoresFromTemplateName', {
                        name: template.title,
                      })}
                      to={`/home/template/${template.id}`}
                      icon={<NFIcon icon="puzzle-alt" className="text-xl" />}
                      selected
                      className="mb-1"
                    />
                  )}
                </Await>
              </Suspense>

              <Divider dark narrow />
            </>
          )}

          {/* Collections */}
          <CollectionsMenu />
          <Divider dark narrow />

          {/* Content Items */}
          <ContentItem itemName="global_sidebar_r3" />
          <ContentItem itemName={`community_sidebar_r3_${session.community.name}`} />

          {/* Redeemed Content */}
          <RedeemedContentMenu />

          {/* Dashboard */}
          <MenuItem
            label={t('titles.dashboard')}
            to="/home/dashboard"
            icon={<NFIcon icon="chart" className="text-xl" />}
            selected={pathname === '/home/dashboard'}
            className="mt-1 mb-1"
          />

          {/* News */}
          <ActivityMenuItem />

          {/* Messages */}
          <MenuItem
            label={t('titles.messages')}
            to="/home/messages"
            icon={<NFIcon icon="feedback" className="text-xl" />}
            selected={pathname === '/home/messages'}
          />

          {/* Premium Ad */}
          {!session.hasPremiumFeatures() && <PremiumAd />}
        </div>
      </div>
    </>
  )
}

export default MyScoresMenu

interface AsyncStatProps {
  statistics: Promise<PrivateStatisticsResponse>
  name: keyof PrivateStatisticsResponse
}
const AsyncStat: React.FC<AsyncStatProps> = ({ statistics, name }) => (
  <Suspense fallback={null}>
    <Await resolve={statistics}>
      {(stats: PrivateStatisticsResponse) =>
        isPresent(stats[name]) ? formatCommas(stats[name]) : null
      }
    </Await>
  </Suspense>
)
